import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { RouteDialogDirective } from '@app/+modals/route-dialog.directive';
import { ValidationService } from '@app/forms/validation.service';
import { DecodeHtmlStringPipe } from '@app/pipes/decode-html-string.pipe';
import { IntegrationAppModel, IntegrationAppTitle } from '@app/reducers/orm/integration-apps/integration-app.model';
import { getIntegrationAppByTitle } from '@app/reducers/orm/integration-apps/integration-app.selector';
import {
  AuthenticateResponse,
  EmployeeMappingForm,
  IntegrationConfigureResponse,
  IntegrationContractSync,
  IntegrationContractSyncDefaultValues,
  IntegrationEmployeeSync,
  IntegrationEmployeeSyncDefaultValues,
} from '@app/reducers/orm/integration/integration-configure.model';
import type { IntegrationConfigureRequest } from '@app/reducers/orm/integration/integration-request.model';
import { IntegrationService } from '@app/reducers/orm/integration/integration.service';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { mgcIconInformationRegular } from '@sb/mgc-icons/mgcIcon-information_regular.icon';
import { TooltipDirective } from '@sb/tooltip';
import { ButtonComponent } from '@sb/ui/button';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { SbDialogModule } from '@sb/ui/dialog';
import { LoadingStateComponent } from '@sb/ui/feedback';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { InputDirective } from '@sb/ui/input';
import { ToastService } from '@sb/ui/toast';
import { SharedLaddaModule } from '@shared/shared-ladda.module';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import { of as observableOf, Subscription } from 'rxjs';
import { finalize, switchMap, take, tap } from 'rxjs/operators';

import { appConfigureConfig, ConfigureAction, ConfigureConfig, ConfigureMode } from './app-configure.model';
import { ContractSyncComponent } from './contract-sync/contract-sync.component';
import { DefaultEmployeeMappingComponent } from './default-employee-mapping/default-employee-mapping.component';
import { EmployeeSyncComponent } from './employee-sync/employee-sync.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'modal-configure',
  templateUrl: './app-configure.component.html',
  imports: [
    RouteDialogDirective,
    SbDialogModule,
    SbFormFieldComponent,
    InputDirective,
    ButtonComponent,
    LoadingStateComponent,
    TranslatePipe,
    IconComponent,
    ReactiveFormsModule,
    SharedLaddaModule,
    DecodeHtmlStringPipe,
    ContractSyncComponent,
    EmployeeSyncComponent,
    DefaultEmployeeMappingComponent,
    TooltipDirective,
    CheckboxComponent,
  ],
})
export class AppConfigureComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private integrationService = inject(IntegrationService);
  private store = inject<Store<AppState>>(Store);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private toast = inject(ToastService);

  @ViewChild(RouteDialogDirective, { static: true })
  dialog: RouteDialogDirective;

  action: ConfigureAction;
  mode: ConfigureMode;
  isLoading: boolean;

  integration: IntegrationAppModel | undefined;
  integrationConfig: IntegrationConfigureResponse;
  appConfig: ConfigureConfig;
  authenticateResponse: AuthenticateResponse;

  configureAction = ConfigureAction;
  configureMode = ConfigureMode;

  showPastStartDateSetting = false;

  authenticateForm = new UntypedFormGroup({});
  configureForm = new UntypedFormGroup({
    mappingName: new UntypedFormControl('', [Validators.required]),
  });

  private dataSubs = new Subscription();

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconInformationRegular]);
  }

  ngOnInit() {
    const currentApp = this.route.snapshot.paramMap.get('app');
    this.appConfig = appConfigureConfig[currentApp];
    this.showPastStartDateSetting = !!this.appConfig.usePastStartDateOnImportEmployee;

    this.setupForms();

    this.dataSubs.add(
      this.store.select(getIntegrationAppByTitle(currentApp as IntegrationAppTitle)).subscribe({
        next: (integration) => {
          this.integration = integration;
        },
      }),
    );

    this.dataSubs.add(
      this.route.paramMap
        .pipe(
          tap((paramMap: ParamMap) => {
            this.mode = paramMap.has('id') ? ConfigureMode.EDIT : ConfigureMode.ADD;
            this.action = ConfigureAction.CONFIGURE;
            if (paramMap.has('id')) {
              this.isLoading = true;
              return;
            }

            if (!this.appConfig.authForm?.length) {
              return;
            }
            this.action = ConfigureAction.AUTHENTICATE;
          }),
          switchMap((paramMap: ParamMap) => {
            if (paramMap.has('id')) {
              return this.integrationService.fetchIntegration(paramMap.get('id'));
            }
            return observableOf(null);
          }),
        )
        .subscribe({
          next: (integrationConfig) => {
            if (!integrationConfig) {
              return;
            }
            this.setupConfig(integrationConfig);
          },
        }),
    );
  }

  authenticate() {
    this.authenticateForm.markAllAsTouched();

    if (!this.authenticateForm.valid) {
      return;
    }

    this.isLoading = true;

    const data: IntegrationConfigureRequest = {
      api_app_id: this.appConfig.appId,
      config: {
        ...this.authenticateForm.value,
      },
    };

    if (this.mode === ConfigureMode.EDIT) {
      data['id'] = parseInt(this.integrationConfig.id, 10);
    }

    this.dataSubs.add(
      this.integrationService.authenticateIntegration(data).subscribe({
        next: (response: AuthenticateResponse) => {
          this.action = ConfigureAction.CONFIGURE;
          this.authenticateResponse = response;

          if (response.default_employee_mapping) {
            const mappingGroup = new FormGroup<EmployeeMappingForm>({
              employee_import: new FormControl(response.default_employee_mapping.employee_import),
              timesheet_export: new FormControl(response.default_employee_mapping.timesheet_export),
              absence_export: new FormControl(response.default_employee_mapping.absence_export),
            });

            this.configureForm.addControl('default_employee_mapping', mappingGroup);
          }

          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          console.error(error);
        },
      }),
    );
  }

  // Tech-debt: should be refactored when we create a common app configure for all our integrations
  // eslint-disable-next-line max-lines-per-function
  configure() {
    this.configureForm.markAllAsTouched();

    if (!this.configureForm.valid) {
      return;
    }

    this.isLoading = true;

    const configureForm = this.configureForm.value;

    let config: Record<string, unknown> = {
      ...omit(configureForm, ['mappingName', 'sync_contract', 'sync_employee']),
      ...configureForm.sync_contract,
      ...configureForm.sync_employee,
      ...configureForm.default_employee_mapping,
    };

    if (this.appConfig.authKeysInConfigure?.length && this.authenticateForm.valid) {
      config = {
        ...config,
        ...pick(this.authenticateForm.value, this.appConfig.authKeysInConfigure),
      };
    }

    const { contractFields, employeeFields } = this.appConfig;

    if (contractFields?.length) {
      config = {
        ...config,
        ...this.authenticateForm.value,
        ...configureForm.sync_contract,
      };
    }

    if (employeeFields?.length) {
      config = {
        ...config,
        ...configureForm.sync_employee,
      };
    }

    const data: IntegrationConfigureRequest = {
      api_app_id: this.appConfig.appId,
      name: configureForm.mappingName,
      config,
    };

    if (this.mode === ConfigureMode.ADD) {
      this.dataSubs.add(
        this.integrationService
          .configureIntegration(data)
          .pipe(finalize(() => this.integrationService.load().pipe(take(1)).subscribe()))
          .subscribe({
            next: (response) => {
              this.isLoading = false;

              if (!this.appConfig.mappingUrl) {
                this.close();
                return;
              }

              const url =
                'account/integrations/' +
                this.route.snapshot.paramMap.get('app') +
                '/mapping/' +
                response.Integration.id +
                '/' +
                this.appConfig.mappingUrl;

              this.close();
              void this.router.navigateByUrl(url);
            },
            error: (error) => {
              this.isLoading = false;
              this.close();
              console.error(error);
            },
          }),
      );

      return;
    }
    data['id'] = this.integrationConfig.id;

    this.dataSubs.add(
      this.integrationService
        .updateIntegration(data)
        .pipe(switchMap(() => this.integrationService.load().pipe(take(1))))
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.close();
          },
          error: (error) => {
            this.isLoading = false;
            this.close();
            console.error(error);
          },
        }),
    );
  }

  ngOnDestroy() {
    this.dataSubs.unsubscribe();
  }

  editAuthenticationInformation() {
    this.authenticateForm.reset();
    this.action = ConfigureAction.AUTHENTICATE;
  }

  close() {
    this.dialog.close();
  }

  private setupForms() {
    this.appConfig.authForm?.forEach((form) => {
      this.authenticateForm.addControl(
        form.name,
        new UntypedFormControl('', form.validators.length ? form.validators : []),
      );
    });

    this.configureForm = new UntypedFormGroup({
      mappingName: new UntypedFormControl('', [Validators.required]),
    });

    this.appConfig.configureForm?.forEach((form) => {
      this.configureForm.addControl(
        form.name,
        new UntypedFormControl('', form.validators?.length ? form.validators : []),
      );
    });

    if (this.appConfig.contractFields?.length > 0) {
      this.configureForm.addControl(
        'sync_contract',
        new UntypedFormControl(pick(IntegrationContractSyncDefaultValues, this.appConfig.contractFields)),
      );
    }

    if (this.appConfig.employeeFields?.length > 0) {
      this.configureForm.addControl(
        'sync_employee',
        new UntypedFormControl(pick(IntegrationEmployeeSyncDefaultValues, this.appConfig.employeeFields)),
      );
    }

    if (this.appConfig.autoAddEmployees) {
      this.configureForm.addControl('auto_add_employees', new UntypedFormControl(false));
    }

    if (this.appConfig.importVat) {
      this.configureForm.addControl('importVat', new UntypedFormControl(this.appConfig.importVat.defaultValue));
    }

    if (this.showPastStartDateSetting) {
      this.configureForm.addControl('use_past_start_date_on_import_employee', new UntypedFormControl(false));
    }

    if (this.appConfig.notifyImport) {
      this.configureForm.addControl(
        'notify_import',
        new UntypedFormControl('', [Validators.required, ValidationService.emailValidator]),
      );
    }

    if (this.appConfig.notifyExport) {
      this.configureForm.addControl(
        'notify_export',
        new UntypedFormControl('', [Validators.required, ValidationService.emailValidator]),
      );
    }

    if (this.appConfig.turnOverImportEmail) {
      this.configureForm.addControl(
        'turnover_import_email',
        new UntypedFormControl('', [Validators.required, ValidationService.emailValidator]),
      );
    }
  }

  private setupConfig(integrationConfig: IntegrationConfigureResponse) {
    this.integrationConfig = integrationConfig;
    const configureForm = integrationConfig.config;

    if (configureForm?.default_employee_mapping) {
      const mappingGroup = new FormGroup<EmployeeMappingForm>({
        employee_import: new FormControl(configureForm.default_employee_mapping.employee_import),
        timesheet_export: new FormControl(configureForm.default_employee_mapping.timesheet_export),
        absence_export: new FormControl(configureForm.default_employee_mapping.absence_export),
      });

      this.configureForm.addControl('default_employee_mapping', mappingGroup);
    }
    const contractSync = pick(configureForm, Object.keys(IntegrationContractSync));
    const employeeSync = pick(configureForm, Object.keys(IntegrationEmployeeSync));

    this.configureForm.patchValue({
      mappingName: integrationConfig.name,
      ...configureForm,
      sync_contract: contractSync,
      sync_employee: employeeSync,
    });

    this.authenticateForm.patchValue(configureForm);
    this.isLoading = false;

    if (integrationConfig.authenticated || !this.appConfig.authForm?.length) {
      this.action = ConfigureAction.CONFIGURE;
      return;
    }
    this.toast.openDanger(this.translate.instant('Your credentials have expired'));
    this.action = ConfigureAction.AUTHENTICATE;
  }

  get displayEmployeeDefaultMapping(): boolean {
    return (
      (this.mode === ConfigureMode.ADD && !!this.authenticateResponse?.default_employee_mapping) ||
      (this.mode === ConfigureMode.EDIT && !!this.integrationConfig?.config?.default_employee_mapping)
    );
  }
}
